import { api, type ExchangeRateInfo, type SupplierInvoiceList, type SupplierInvoiceRow } from '../api';
import { callout, field, input, opt, openDialog, select, todayISO, val } from '../dialog';
import { esc, fmtDate, html, pill, toast } from '../ui';

/**
 * The invoices we receive, on the screen that already deals with money.
 *
 * Beside the ones we issue rather than on a page of their own, because the
 * question people actually have is about both at once: what is owed to us and
 * what we owe. Splitting them across two screens means nobody ever sees the
 * whole picture.
 *
 * Two currencies are shown on every line — theirs and ours — with the rate
 * between them. A converted figure on its own cannot be checked against the
 * paper, which is what somebody is holding when they look at this.
 */

const STATUS: Record<string, { tone: 'OK' | 'DUE_SOON' | 'OVERDUE' | 'NA'; label: string }> = {
  RECEIVED: { tone: 'DUE_SOON', label: 'to approve' },
  APPROVED: { tone: 'DUE_SOON', label: 'approved' },
  PAID: { tone: 'OK', label: 'paid' },
  DISPUTED: { tone: 'OVERDUE', label: 'disputed' },
};

const money = (n: number, currency: string) =>
  `${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;

function row(i: SupplierInvoiceRow): string {
  const s = STATUS[i.status] ?? { tone: 'NA' as const, label: i.status.toLowerCase() };
  const converted = i.exchangeRate !== null;

  return html`
    <tr${i.status === 'PAID' ? ' style="opacity:.6"' : ''}>
      <td>
        <b>${esc(i.supplier)}</b>
        <div class="tiny dim">
          ${esc(i.number)}
          ${i.attachment ? ' · scan on file' : ' · no scan'}
          ${i.workOrder ? ` · ${esc(i.workOrder.reference)}` : ''}
          ${i.aircraft ? ` · ${esc(i.aircraft.registration)}` : ''}
        </div>
      </td>
      <td class="tiny">
        ${esc(fmtDate(i.issuedAt))}
        ${i.dueAt ? html`<div class="dim">due ${esc(fmtDate(i.dueAt))}</div>` : ''}
      </td>
      <td class="n">
        <b>${esc(money(i.grossAmount, i.currency))}</b>
        ${converted
          ? html`<div class="tiny dim">
              ${esc(money(i.grossInOwnCurrency, i.ownCurrency))} at ${i.exchangeRate!.toFixed(4)}
            </div>`
          : ''}
      </td>
      <td>
        ${pill(i.overdue ? 'OVERDUE' : s.tone, i.overdue ? 'overdue' : s.label)}
        ${i.paymentRef ? html`<div class="tiny dim">${esc(i.paymentRef)}</div>` : ''}
      </td>
      <td style="white-space:nowrap;text-align:right">
        ${i.status === 'RECEIVED'
          ? html`<button class="btn sm" data-si-approve="${esc(i.id)}">Approve</button>`
          : ''}
        ${i.status !== 'PAID'
          ? html`<button class="btn sm" data-si-paid="${esc(i.id)}" data-label="${esc(`${i.supplier} ${i.number}`)}">Paid</button>`
          : ''}
      </td>
    </tr>
  `;
}

export function supplierInvoicesCard(list: SupplierInvoiceList | null): string {
  if (!list) return '';
  const { summary } = list;

  return html`
    <details class="card" data-supplier-invoices${summary.awaitingApproval || summary.overdue ? ' open' : ''}>
      <summary class="card-h">
        <div class="sum">
          <span class="twist" aria-hidden="true"></span>
          <div>
            <h2>Invoices we have received</h2>
            <p class="tiny dim">
              What we owe — suppliers, shops and outside maintenance. Recorded in the currency they
              billed in, with the rate that was applied.
            </p>
          </div>
        </div>
        <div style="display:flex;gap:6px;align-items:center">
          ${summary.overdue
            ? pill('OVERDUE', `${summary.overdue} overdue`)
            : summary.unpaid
              ? pill('DUE_SOON', `${summary.unpaid} unpaid`)
              : pill('OK', 'nothing outstanding')}
          <button class="btn sm pri" data-si-record>Record one</button>
        </div>
      </summary>

      <div class="card-b">
        ${summary.count
          ? html`
              <p class="tiny dim" style="margin:0 0 10px">
                ${summary.unpaid} unpaid, ${esc(money(summary.unpaidValue, list.ownCurrency))} in
                ${esc(list.ownCurrency)}${summary.overdue
                  ? ` · ${summary.overdue} past due, ${esc(money(summary.overdueValue, list.ownCurrency))}`
                  : ''}${summary.awaitingApproval ? ` · ${summary.awaitingApproval} awaiting approval` : ''}
              </p>
              <div class="scrollx">
                <table class="tbl">
                  <thead>
                    <tr><th>Supplier</th><th>Dated</th><th class="n">Amount</th><th>Status</th><th></th></tr>
                  </thead>
                  <tbody>${list.invoices.map(row).join('')}</tbody>
                </table>
              </div>
            `
          : html`<div class="muted">Nothing recorded yet.</div>`}
      </div>

      <div class="card-f">
        The same supplier and number cannot be entered twice — that is the mistake that pays a bill
        two times. Approving and paying are separate acts, and marking one paid asks for the bank
        reference, because "paid" with nothing to look up is not an answer.
      </div>
    </details>
  `;
}

/** Recording one. */
function recordDialog(exchange: ExchangeRateInfo | undefined, reload: () => void): void {
  const own = exchange?.quote ?? 'EUR';

  openDialog<Record<string, unknown>>({
    wide: true,
    title: 'Record an invoice we received',
    sub: 'As the supplier wrote it — the conversion is worked out from the rate on file',
    primary: 'Record it',
    body: () => html`
      <div class="frow">
        ${field('Supplier', input('supplier', '', 'maxlength="160" placeholder="Lufthansa Technik AG"'))}
        ${field('Their invoice number', input('number', '', 'maxlength="60" style="font-family:var(--mono)"'),
          'Theirs, not ours — it is what an accountant will ask for')}
      </div>
      <div class="frow3">
        ${field('Dated', input('issuedAt', todayISO(), 'type="date"'))}
        ${field('Due', input('dueAt', '', 'type="date"'), 'Leave blank if their terms are not stated')}
        ${field('Currency', select('currency', ['RON', 'EUR', 'USD', 'GBP', 'CHF']
          .map((c) => opt(c, c, c === own)).join(''), 'data-si-currency'))}
      </div>
      <div class="frow3">
        ${field('Net', input('netAmount', '', 'inputmode="decimal" style="text-align:right;font-family:var(--mono)"'))}
        ${field('VAT', input('vatAmount', '', 'inputmode="decimal" style="text-align:right;font-family:var(--mono)"'))}
        ${field('Their total', input('grossAmount', '', 'inputmode="decimal" style="text-align:right;font-family:var(--mono)"'),
          'Leave blank for net + VAT. Where their paper says otherwise, theirs wins')}
      </div>
      ${field('Notes', input('notes', '', 'maxlength="4000" placeholder="Wheel overhaul, exchange unit"'))}
      <div data-si-rate-note></div>
    `,
    onRender: (root) => {
      const currency = root.querySelector<HTMLSelectElement>('[data-si-currency]');
      const note = root.querySelector<HTMLElement>('[data-si-rate-note]');
      if (!currency || !note) return;

      // Said while the currency is being chosen, not after the refusal.
      const describe = () => {
        const chosen = currency.value;
        if (chosen === own) {
          note.innerHTML = callout('info', 'i', html`
            Already in ${esc(own)}, so there is nothing to convert.
          `);
          return;
        }
        const usable = exchange && exchange.base === chosen && exchange.quote === own && exchange.rate !== null;
        note.innerHTML = usable
          ? callout('info', 'i', html`
              Converted at <b>1 ${esc(chosen)} = ${exchange!.rate!.toFixed(4)} ${esc(own)}</b>. The rate is
              stored on the invoice, so what this purchase cost will not move when the rate does.
            `)
          : callout('caution', '!', html`
              There is no <b>${esc(chosen)} → ${esc(own)}</b> rate on file, so this cannot be recorded in
              ${esc(chosen)} yet. Set one on the exchange rate card above.
            `);
      };
      currency.addEventListener('change', describe);
      describe();
    },
    collect: (root) => {
      const number = (value: string) => Number(val(root, value).replace(',', '.'));
      const supplier = val(root, 'supplier').trim();
      const invoiceNumber = val(root, 'number').trim();
      if (!supplier) return { error: 'Who sent it?' };
      if (!invoiceNumber) return { error: 'Their invoice number is what identifies it.' };

      const netAmount = number('netAmount');
      if (!(netAmount >= 0) || Number.isNaN(netAmount)) return { error: 'Give the net amount.' };
      const vatAmount = val(root, 'vatAmount') ? number('vatAmount') : 0;
      if (Number.isNaN(vatAmount)) return { error: 'That VAT amount is not a number.' };
      const grossRaw = val(root, 'grossAmount');
      const grossAmount = grossRaw ? number('grossAmount') : undefined;
      if (grossAmount !== undefined && Number.isNaN(grossAmount)) return { error: 'That total is not a number.' };
      if ((grossAmount ?? netAmount + vatAmount) <= 0) return { error: 'An invoice for nothing is not an invoice.' };

      return {
        supplier,
        number: invoiceNumber,
        issuedAt: val(root, 'issuedAt') || todayISO(),
        dueAt: val(root, 'dueAt') || undefined,
        currency: val(root, 'currency'),
        netAmount,
        vatAmount,
        grossAmount,
        notes: val(root, 'notes') || undefined,
      };
    },
    submit: (payload) => api.billing.recordSupplierInvoice(payload),
    done: (r: { supplier: string; number: string; grossAmount: number; currency: string }) =>
      `${r.supplier} ${r.number} recorded — ${money(r.grossAmount, r.currency)}`,
    onDone: reload,
  });
}

/** Marking one paid asks for the reference, because that is the answer. */
function markPaidDialog(id: string, label: string, reload: () => void): void {
  openDialog<{ paymentRef: string; paidAt: string }>({
    title: 'Mark it paid',
    sub: label,
    primary: 'Mark paid',
    body: () => html`
      <div class="frow">
        ${field('Bank reference', input('paymentRef', '', 'maxlength="80" style="font-family:var(--mono)" placeholder="OP 4471/2026"'),
          'What somebody would search for in the statement')}
        ${field('Paid on', input('paidAt', todayISO(), 'type="date"'))}
      </div>
    `,
    collect: (root) => {
      const paymentRef = val(root, 'paymentRef').trim();
      if (!paymentRef) return { error: 'The reference is the point — without it "paid" cannot be checked.' };
      return { paymentRef, paidAt: val(root, 'paidAt') || todayISO() };
    },
    submit: (p) => api.billing.setSupplierInvoiceStatus(id, { status: 'PAID', ...p }),
    done: () => 'Marked paid',
    onDone: reload,
  });
}

export function bindSupplierInvoices(
  host: HTMLElement,
  exchange: ExchangeRateInfo | undefined,
  reload: () => void,
): void {
  host.querySelector<HTMLButtonElement>('[data-si-record]')?.addEventListener('click', (e) => {
    e.preventDefault();
    recordDialog(exchange, reload);
  });

  host.querySelectorAll<HTMLButtonElement>('[data-si-approve]').forEach((b) =>
    b.addEventListener('click', () => {
      void api.billing
        .setSupplierInvoiceStatus(b.dataset.siApprove!, { status: 'APPROVED' })
        .then(() => {
          toast('Approved for payment');
          reload();
        })
        .catch((err: Error) => toast(err.message || 'That could not be approved.', 'error'));
    }),
  );

  host.querySelectorAll<HTMLButtonElement>('[data-si-paid]').forEach((b) =>
    b.addEventListener('click', () => markPaidDialog(b.dataset.siPaid!, b.dataset.label ?? '', reload)),
  );
}
